import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { COURIER_RELEASE_ATTRIBUTIONS, COURIER_RELEASE_NOTE_MAX_LENGTH, type CourierDispatchJob, type CourierReleaseAttribution, type CourierReleaseReason } from '@vello/api-client';
import { Button, Field, Notice, Select, TextArea, Row, FormStack } from '@vello/web-theme';
import {
  afterPickupOutcomeFor, afterPickupSentences, defaultReleaseReason, releaseAttributionRequired, releaseNoteRequired, releasePhaseMessageId, releaseReasonsFor, shortId, validateReleaseDraft,
} from '../../src/index';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { FormDialog } from './form-dialog';

const REASON_ID = 'cjobs-release-reason';
const NOTE_ID = 'cjobs-release-note';
const ATTRIBUTION_ID = 'cjobs-release-attribution';

export interface CourierReleaseDialogProps {
  readonly open: boolean;
  /** The job the operator is releasing (the live row while open; the confirm is held once it is no longer releasable). */
  readonly job: CourierDispatchJob | undefined;
  /** A release is in flight: the controls are held and the scrim/Escape do not close. */
  readonly busy: boolean;
  /** The translated refusal of the last attempt; the dialog stays open to correct or retry. */
  readonly error: string | undefined;
  readonly onCancel: () => void;
  readonly onConfirm: (input: { readonly reasonCode: CourierReleaseReason; readonly note?: string; readonly attribution?: CourierReleaseAttribution }) => void;
}

/**
 * The courier release dialog (W4-COURIER-RELEASE, D378) — composed from the design system's own layer (the
 * `vello-scrim` / `vello-dialog-host` / `vello-dialog` classes, the FX-03 `useLayer` choreography, `useEscape`, the
 * `Z_INDEX` and `EFFECT_CLASS` tokens) instead of the shared `ConfirmDialog`, whose 36 px reason chips and 46 px buttons
 * miss the 48 px floor. Every control here is a theme primitive at `--tap-target-min`: the reason `Select`, the note
 * `TextArea`, and the two `Button`s.
 *
 * One primary action (the confirm), disabled until the draft is valid — a reason (`PIN_LOCKED` is offered, and
 * pre-selected, only on a locked handover) and, for `OPERATIONAL_OTHER`, a note. The phase line says what the release
 * will do. Accessible: `alertdialog` + `aria-modal` + labelled/described, focus lands on the reason when it opens, Tab
 * stays inside, Escape and the scrim cancel, and focus returns to the control that opened it.
 */
export function CourierReleaseDialog({ open, job, busy, error, onCancel, onConfirm }: CourierReleaseDialogProps): ReactElement | null {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [reasonCode, setReasonCode] = useState<string>('');
  const [note, setNote] = useState('');
  // D858: whose error a PIN-locked release was - no default, the operator must say (it decides who pays).
  const [attribution, setAttribution] = useState('');

  const cancel = (): void => {
    if (!busy) onCancel();
  };

  // A fresh draft per opening (per job): the locked handover's reason pre-selected, no note.
  const jobId = job?.jobId;
  const pinLocked = job?.pinLocked === true || job?.pickupLocked === true;
  useEffect(() => {
    if (!open) return;
    setReasonCode(defaultReleaseReason({ pinLocked }));
    setNote('');
    setAttribution('');
  }, [open, jobId, pinLocked]);

  if (job === undefined) return null;

  const reasons = releaseReasonsFor(job);
  const draft = validateReleaseDraft({ reasonCode, note, attribution }, job);
  const needsAttribution = releaseAttributionRequired(reasonCode);
  const needsNote = releaseNoteRequired(reasonCode);
  const order = job.orderCode ?? shortId(job.sourceOrderId);
  const afterPickup = job.release.phase === 'after_pickup';
  // D856 (owner R2): a rider Send already picked up says, BEFORE the click, what the market's rule does to the money for the reason picked.
  const money = (m: { readonly amountMinor: string; readonly currency: string }): string =>
    /^\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : '—';
  const outcome = afterPickup ? afterPickupOutcomeFor(job.release.afterPickup, reasonCode, attribution) : undefined;

  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (busy || !draft.ok || !job.release.allowed) return;
    onConfirm({ reasonCode: draft.reasonCode, ...(draft.note !== undefined ? { note: draft.note } : {}), ...(draft.attribution !== undefined ? { attribution: draft.attribution } : {}) });
  };

  return (
    <FormDialog open={open} id="cjobs-release" icon={afterPickup ? 'ban' : 'rotateCcw'} tone={afterPickup ? 'danger' : 'warning'} title={t('cjobs.release.title', { order })} body={t('cjobs.release.body')} busy={busy} initialFocusId={REASON_ID} onCancel={onCancel}>
      <Notice tone={afterPickup ? 'warning' : 'neutral'}>{t(releasePhaseMessageId(job.release.phase))}</Notice>
      {outcome !== undefined && job.release.afterPickup !== undefined && (
        <Notice tone="neutral">
          <strong>{t('cjobs.after.title')}</strong>
          <ul data-testid="cjobs-after-pickup">
            {afterPickupSentences(outcome, money).map((s) => (
              <li key={s.messageId}>{t(s.messageId, s.params)}</li>
            ))}
          </ul>
          <small>{t('cjobs.after.policy', { version: job.release.afterPickup.policyVersion })}</small>
        </Notice>
      )}
      <FormStack gap={4} onSubmit={submit} noValidate>
        <Field label={t('cjobs.release.reason')} htmlFor={REASON_ID}>
          <Select id={REASON_ID} value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} required aria-required="true" disabled={busy}>
            <option value="">{t('cjobs.release.reason.choose')}</option>
            {reasons.map((code) => (
              <option key={code} value={code}>{v('courier.release_reason', code)}</option>
            ))}
          </Select>
        </Field>
        {needsAttribution && (
          <Field label={t('cjobs.release.attribution')} htmlFor={ATTRIBUTION_ID} hint={t('cjobs.release.attribution.hint')}>
            <Select id={ATTRIBUTION_ID} value={attribution} onChange={(e) => setAttribution(e.target.value)} required aria-required="true" disabled={busy}>
              <option value="">{t('cjobs.release.attribution.choose')}</option>
              {COURIER_RELEASE_ATTRIBUTIONS.map((code) => (
                <option key={code} value={code}>{v('courier.release_attribution', code)}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field
          label={t(needsNote ? 'cjobs.release.noteRequired' : 'cjobs.release.note')}
          htmlFor={NOTE_ID}
          hint={t(needsNote ? 'cjobs.release.note.requiredHint' : 'cjobs.release.note.hint', { max: COURIER_RELEASE_NOTE_MAX_LENGTH })}
        >
          <TextArea
            id={NOTE_ID}
            value={note}
            maxLength={COURIER_RELEASE_NOTE_MAX_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            required={needsNote}
            aria-required={needsNote}
            disabled={busy}
            rows={3}
          />
        </Field>
        {error !== undefined && (
          <div>
            <Notice tone="danger">{error}</Notice>
          </div>
        )}
        <Row gap={2} align="stretch">
          <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={cancel} disabled={busy}>
            {t('ui.cancel')}
          </Button>
          <Button className="vello-admin-a-btn-grow" type="submit" variant="primary" disabled={busy || !draft.ok || !job.release.allowed}>
            {busy ? t('cjobs.release.sending') : t('cjobs.release.confirm')}
          </Button>
        </Row>
      </FormStack>
    </FormDialog>
  );
}
